import { Box, Popover, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, { ReactElement, ReactNode, useRef, useState } from 'react';

const COLORS = {
  border: '#DEDEDE',
  text: '#383838',
  selectedBg: '#F5F6F8',
} as const;

export interface FilterDropdownOption<T> {
  value: T;
  label: string;
  icon?: ReactNode;
}

export interface FilterDropdownTriggerProps {
  open: boolean;
  disabled: boolean;
  label: string;
  onOpen: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}

export interface FilterDropdownProps<T> {
  value: T;
  options: FilterDropdownOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  width?: string | number | Record<string, string | number>;
  renderTrigger?: (props: FilterDropdownTriggerProps) => ReactNode;
}

export function FilterDropdown<T> ({
  value,
  options,
  onChange,
  disabled = false,
  id,
  ariaLabel,
  width = '100%',
  renderTrigger,
}: FilterDropdownProps<T>): ReactElement {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const selected = options.find(option => option.value === value);
  const label = selected?.label ?? '';

  const onOpen = () => {
    if (disabled) return;
    setOpen(true);
  };

  const handleSelect = (next: T) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger({
          open,
          disabled,
          label,
          onOpen,
          anchorRef,
        })
      ) : (
        <Box
          ref={anchorRef}
          id={id}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-disabled={disabled}
          onClick={onOpen}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onOpen();
            }
          }}
          sx={{
            width,
            minWidth: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              height: '50px',
              width: '100%',
              px: '16px',
              backgroundColor: '#FFFFFF',
              boxSizing: 'border-box',
              '&:hover': disabled
                ? undefined
                : { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: 'Poppins, sans-serif',
                lineHeight: '21px',
                color: COLORS.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: 1,
                minWidth: 0,
              }}
            >
              {label}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{ fontSize: 24, color: COLORS.text, flexShrink: 0 }}
            />
          </Box>
        </Box>
      )}

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: '4px',
            borderRadius: '8px',
            boxShadow: '0px 4px 16px rgba(0,0,0,0.12)',
            maxHeight: 300,
            overflowY: 'auto',
            minWidth: 220,
            width: anchorRef.current?.offsetWidth || undefined,
          },
        }}
      >
        {options.map(option => {
          const isSelected = option.value === value;
          return (
            <Box
              key={String(option.value)}
              role="option"
              aria-selected={isSelected}
              onClick={() => handleSelect(option.value)}
              sx={{
                px: '16px',
                py: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                backgroundColor: isSelected ? COLORS.selectedBg : 'white',
                '&:hover': { backgroundColor: COLORS.selectedBg },
              }}
            >
              {option.icon}
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: isSelected ? 500 : 400,
                  fontFamily: 'Poppins, sans-serif',
                  color: COLORS.text,
                  lineHeight: '21px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.label}
              </Typography>
            </Box>
          );
        })}
      </Popover>
    </>
  );
}
